/*
 * Invoices, the best of Jobber and the old Steward (owner, 2026-10-29). The list: Jobber's Overview with the money next to
 * each status (each one filters), Issued, Average invoice and Payment time against the 30 days before, then Status and Date
 * pills and every invoice with its due day aging in words. One invoice: "Invoice for Jane for $50", the client beside the
 * details, Product / Service with Edit, totals down to the balance, then payments, how they can pay (this invoice only),
 * put it on their other invoice, who worked it, the message to the client and Activity. The Send box shows the exact
 * message and who gets it, Collect payment takes cash and checks, Edit asks Credit or Don't credit when a paid invoice
 * goes down, and Charge saved card is for the owner with a confirm. Payments by day sit on their own page.
 */
// What the invoice screens share: the status tag and look, day and money helpers, the banners.
import {
  addDays,
  CHECK_WAIT_DAYS,
  INVOICE_STATE_WORDS,
  type InvoiceDetail,
  type InvoiceFilter,
  type InvoiceRow,
  type InvoiceState,
} from "@steward/shared";
import { useAutoChargeHold } from "../../api/money";
import { useSession } from "../../app/session";
import { moneyExact, plural, shortDate } from "../../components/format";
import { useToast } from "../../components/overlays";
import { Banner, Button, type Status, StatusLabel } from "../../components/parts";
import { errText } from "../../components/work";

const STATE_LOOK: Record<InvoiceState, Status> = {
  to_check: "informative",
  awaiting: "warning",
  past_due: "critical",
  debt: "critical",
  paid: "success",
  closed: "inactive",
};

export const InvoiceTag = ({ state }: { state: InvoiceState }) => (
  <StatusLabel status={STATE_LOOK[state]}>{INVOICE_STATE_WORDS[state]}</StatusLabel>
);

export const tokenOf = (link: string) => link.split("/").pop() ?? "";

export const dollars = (s: string): number | null => {
  const n = Number(s.replace(/[$,\s]/g, ""));
  return s.trim() && Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
};

/** An invoice waiting to charge the saved card by itself: when, with Hold (the old autobill). */

/** The client said a check is in the mail: reminders wait until the check comes or 10 days pass. */
export function CheckMailBanner({ at, tz }: { at: string; tz: string }) {
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(at));
  return (
    <Banner type="notice">
      Check in the mail since {shortDate(day)}. Reminders wait until {shortDate(addDays(day, CHECK_WAIT_DAYS))}. Use Collect
      payment when it comes.
    </Banner>
  );
}

export function AutoChargeBanner({ i }: { i: InvoiceDetail }) {
  const { company, can } = useSession();
  const hold = useAutoChargeHold();
  const toast = useToast();
  const card = i.client.card ? `card ending ${i.client.card.last4}` : "saved card";
  const at = i.autoChargeAt ? new Date(i.autoChargeAt) : null;
  const when =
    at && at.getTime() > Date.now()
      ? `at ${at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: company.timezone })}`
      : "in a minute";
  const press = (on: boolean) =>
    hold.mutate(
      { id: i.id, hold: on },
      {
        onSuccess: () => toast(on ? "Held. It waits for you." : "It will charge the card in a minute."),
        onError: (e: unknown) => toast(errText(e), { kind: "error" }),
      },
    );
  if (i.autoChargeHeld)
    return (
      <Banner
        type="warning"
        action={
          can("card.charge") ? (
            <Button type="secondary" size="small" loading={hold.isPending} onClick={() => press(false)}>
              Charge it
            </Button>
          ) : null
        }
      >
        Held. The {card} is not charged until you take the hold off or send the invoice.
      </Banner>
    );
  return (
    <Banner
      type="notice"
      action={
        can("invoice.update") ? (
          <Button type="secondary" size="small" loading={hold.isPending} onClick={() => press(true)}>
            Hold
          </Button>
        ) : null
      }
    >
      The {card} is charged {when} for {moneyExact(i.balanceCents)}. Change the price or press Hold first if you need to.
    </Banner>
  );
}

/** The list's date filter, as the old Steward's (09-lists.js LRANGES). */
export const RANGES = [
  ["all", "Any time"],
  ["7", "Past 7 days"],
  ["30", "Past 30 days"],
  ["month", "This month"],
  ["lastmonth", "Last month"],
  ["year", "This year"],
] as const;

export type Range = (typeof RANGES)[number][0];

export function rangeDays(range: Range, today: string): { from?: string; to?: string } {
  const first = `${today.slice(0, 8)}01`;
  if (range === "7" || range === "30") return { from: addDays(today, -Number(range)) };
  if (range === "month") return { from: first };
  if (range === "lastmonth") {
    const last = addDays(first, -1);
    return { from: `${last.slice(0, 8)}01`, to: last };
  }
  if (range === "year") return { from: `${today.slice(0, 4)}-01-01` };
  return {};
}

/** The day a moment falls on in the company's time zone. */
export const dayIn = (tz: string, iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(iso));

const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);

/**
 * When an invoice is due, aging in words (owner rule 4a, things age visibly): "Oct 30", "Today", "3 days late". Late only
 * while money is still owed; paid and closed ones show the day quietly.
 */
export function dueOf(i: InvoiceRow, tz: string, today: string): { text: string; late: boolean } {
  if (!i.dueAt) return { text: i.state === "to_check" ? "Set when sent" : "Upon receipt", late: false };
  const day = dayIn(tz, i.dueAt);
  const open = i.balanceCents > 0 && (i.state === "awaiting" || i.state === "past_due" || i.state === "debt");
  const late = open ? daysBetween(day, today) : 0;
  if (late > 0) return { text: `${plural(late, "day")} late`, late: true };
  if (open && day === today) return { text: "Today", late: false };
  return { text: shortDate(day), late: false };
}

/**
 * The status words an invoice shows, like the old Steward's: To collect, Scheduled and Payment on the way first. One dot
 * for the status; Viewed and Not sent yet are small tinted flags after it, only while money is still owed.
 */
export function InvoiceStatus({ i }: { i: InvoiceRow }) {
  const open = i.state === "awaiting" || i.state === "past_due";
  return (
    <span className="chips tight">
      {i.status === "to_collect" ? (
        <StatusLabel status="warning">To collect</StatusLabel>
      ) : i.status === "scheduled" ? (
        <StatusLabel status="informative">Scheduled</StatusLabel>
      ) : i.onTheWay && open ? (
        <StatusLabel status="informative">Payment on the way</StatusLabel>
      ) : (
        <InvoiceTag state={i.state} />
      )}
      {i.notSent && open ? <span className="status flag orange">Not sent yet</span> : null}
      {i.viewed && open ? <span className="status flag informative">Viewed</span> : null}
    </span>
  );
}

export type Pick = InvoiceFilter | "all";
